import { produce } from 'immer';
import { difference, findIndex, remove } from 'lodash/fp';
import { extractError } from 'app/lib/error';
import { toStringFqn } from 'app/lib/utils';
import { ADD_TAGS, REMOVE_TAGS } from 'app/store/types.js';
import { SET_ATTRIBUTE_VALUE, DELETE_ATTRIBUTE_VALUE } from 'app/store/attributes/types.js';
import { UPDATE_TABLE_DESCRIPTION_SUCCESS_ASSET_DESCRIPTION_UPDATED } from 'app/store/__generated__/events';
import { DELETE_SNOWFLAKE_TABLE } from 'app/store/snowflake/types.js';
import {
    GET_PROJECT_REFERENCES_OF_TABLE,
    GET_TABLE_DETAILS_FOR_DATA_BROWSER,
    LOAD_TABLE_DETAILS,
    GET_DOWNSTREAM_REFERENCES
} from './types.js';

export const INITIAL_STATE = {
    byFqn: {},
    projectReferencesByTableFqn: {},
    downstreamReferencesByTableFqn: {},
    isLoading: false,
    error: null
};

function ensureEntityExists(draft, fqn) {
    if (!draft.byFqn.hasOwnProperty(fqn)) {
        draft.byFqn[fqn] = {};
    }
}

function storeEntity(draft, entity) {
    ensureEntityExists(draft, entity.fqn);
    Object.assign(draft.byFqn[entity.fqn], entity);
}

export const tableDetailsReducer = produce((draft, action) => {
    switch (action.type) {
        case LOAD_TABLE_DETAILS.STARTED:
        case GET_TABLE_DETAILS_FOR_DATA_BROWSER.STARTED: {
            draft.error = null;
            draft.isLoading = true;
            break;
        }
        case LOAD_TABLE_DETAILS.SUCCEEDED: {
            const { database, schema, table } = action.payload;
            storeEntity(draft, {
                ...action.payload,
                name: table,
                fqn: toStringFqn({ database, schema, table })
            });
            return;
        }
        case GET_TABLE_DETAILS_FOR_DATA_BROWSER.SUCCEEDED: {
            const { details } = action.payload;
            details.forEach((item) => {
                const { database, schema, table } = item;
                storeEntity(draft, {
                    ...item,
                    name: table,
                    fqn: toStringFqn({ database, schema, table })
                });
            });
            return;
        }
        case LOAD_TABLE_DETAILS.FAILED:
        case GET_TABLE_DETAILS_FOR_DATA_BROWSER.FAILED: {
            draft.error = extractError(action.payload);
            break;
        }
        case LOAD_TABLE_DETAILS.ENDED:
        case GET_TABLE_DETAILS_FOR_DATA_BROWSER.ENDED: {
            draft.isLoading = false;
            break;
        }
        case ADD_TAGS.SUCCEEDED: {
            const { database, schema, table } = action.payload.entityId;
            if (database && schema && table) {
                const fqn = toStringFqn({ database, schema, table });
                draft.byFqn[fqn].tags = [...draft.byFqn[fqn].tags, ...action.payload.tags];
            }

            break;
        }
        case REMOVE_TAGS.SUCCEEDED: {
            const { database, schema, table } = action.payload.entityId;
            if (database && schema && table) {
                const fqn = toStringFqn({ database, schema, table });
                draft.byFqn[fqn].tags = difference(draft.byFqn[fqn].tags, action.payload.tags);
            }
            break;
        }
        case SET_ATTRIBUTE_VALUE.SUCCEEDED: {
            const { database, schema, table, column } = action.payload.entityId;

            if (column) {
                return;
            }

            if (database && schema && table) {
                const { payload, definitionUid } = action.payload;
                const fqn = toStringFqn({ database, schema, table });
                const index = findIndex({ uid: definitionUid }, draft.byFqn[fqn].properties);

                draft.byFqn[fqn].properties = draft.byFqn[fqn].properties.map((property) => {
                    if (property.uid === definitionUid) {
                        return { ...property, value: payload.value };
                    }
                    return property;
                });
                if (index < 0) {
                    draft.byFqn[fqn].properties.push({
                        ...action.payload,
                        value: payload.value,
                        uid: definitionUid
                    });
                }
            }
            break;
        }
        case DELETE_ATTRIBUTE_VALUE.SUCCEEDED: {
            const { database, schema, table, column } = action.payload.entityId;

            if (column) {
                return;
            }

            if (database && schema && table) {
                const { definitionUid } = action.payload;
                const fqn = toStringFqn({ database, schema, table });

                draft.byFqn[fqn].properties = remove(
                    { uid: definitionUid },
                    draft.byFqn[fqn].properties
                );
            }

            break;
        }
        case UPDATE_TABLE_DESCRIPTION_SUCCESS_ASSET_DESCRIPTION_UPDATED: {
            const { database, schema, table } = action.payload.assetId;
            const fqn = toStringFqn({ database, schema, table });
            draft.byFqn[fqn].description = action.payload.description;
            break;
        }
        case GET_PROJECT_REFERENCES_OF_TABLE.SUCCEEDED: {
            const { projectReferences } = action.payload;
            const { database, schema, table } = action.params;
            const fqn = toStringFqn({ database, schema, table });

            draft.projectReferencesByTableFqn[fqn] = projectReferences.map((entry) => ({
                name: entry.projectName,
                slug: entry.projectSlug,
                owner: entry.projectOwner
            }));
            break;
        }
        case GET_DOWNSTREAM_REFERENCES.SUCCEEDED: {
            const { references } = action.payload;
            const fqn = toStringFqn(action.params);

            draft.downstreamReferencesByTableFqn[fqn] = references.map((entry) => ({
                name: entry.viewName,
                projectSlug: entry.spaceSlug,
                viewSlug: entry.viewSlug
            }));
            break;
        }
        case DELETE_SNOWFLAKE_TABLE.SUCCEEDED: {
            const { database, schema, table } = action.params;
            const fqn = toStringFqn({ database, schema, table });
            delete draft.projectReferencesByTableFqn[fqn];
            break;
        }

        default:
    }
}, INITIAL_STATE);
